// src/components/doctor/PrescriptionBuilder.jsx
//
// The medicine table of a consultation: drug search, dose / frequency /
// duration / instructions per row, reorder + remove, an advisory allergy and
// duplicate banner, Save draft, PDF, and an on-screen preview.
//
// Pure rules (frequency regex, bounds, payload assembly) live in
// utils/prescription.js. The parent talks to this through a ref:
//   ref.current.ensureSaved() → Promise<string|null>
// which saves pending edits and resolves the draft's id (null when there are
// no medicines), so "Sign & complete" can sign it. The advisory check never
// blocks anything.
//   ref.current.addMedicines(prescription) → {added, duplicates, overLimit}
// copies a previous prescription's rows into the draft (utils/copyMedicines.js).
// `active` is false while the step is hidden: the builder stays mounted but must not
// claim the shared #consult-step-title id (the visible step owns it).
// The parent hears about the rows through onItemsChange (the stepper and the
// review count them) and owns the "No medicines needed" flag.

import React, {
  forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, useCallback,
} from 'react';
import { useToast } from '../../context/ToastContext';
import {
  searchDrugs, createPrescription, updatePrescription, checkPrescription, openPrescriptionPdf,
} from '../../services/prescriptionApi';
import { mergeCopiedMedicines } from '../../utils/copyMedicines';
import {
  FREQUENCY_PRESETS, MAX_ITEMS, itemFromDrug, itemsFromPrescription, validateItems,
  buildPrescriptionBody, moveItem, checkableDrugNames, drugNamesKey, shouldSearchDrugs,
  describeItem, isValidFrequency, frequencyText,
} from '../../utils/prescription';

// The medicine table deliberately does not use .table-wrap: on phones that class
// hides columns 4-5 (days, instructions). .rx-table (index.css) stacks each row into a
// labelled card below 640px instead, so no field needs sideways scrolling.
const SEARCH_DEBOUNCE_MS = 300;
const CHECK_DEBOUNCE_MS = 700;

const errMsg = (err, fallback) => err?.response?.data?.message || fallback;

/* ── Drug search combobox ─────────────────────────────────────────────────── */
function DrugSearch({ disabled, onPick, atLimit }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!shouldSearchDrugs(q)) { setResults([]); setState('idle'); return undefined; }
    const id = ++seq.current;
    setState('loading');
    const t = setTimeout(() => {
      searchDrugs(q.trim())
        .then(r => { if (id === seq.current) { setResults(r); setActive(0); setState('done'); } })
        .catch(() => { if (id === seq.current) { setResults([]); setState('error'); } });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const pick = drug => {
    onPick(drug);
    setQ(''); setResults([]); setOpen(false); setState('idle');
  };
  const addCustom = () => { if (q.trim()) pick({ name: q.trim() }); };

  const onKeyDown = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && results[active]) pick(results[active]);
      else addCustom();
    } else if (e.key === 'Escape') setOpen(false);
  };

  const showList = open && shouldSearchDrugs(q);
  return (
    <div className="form-group" style={{ position: 'relative' }}>
      <label className="form-label" htmlFor="rx-drug-search">Add a medicine</label>
      <input
        id="rx-drug-search" className="form-input" type="text" autoComplete="off"
        role="combobox" aria-expanded={showList} aria-controls="rx-drug-list" aria-autocomplete="list"
        aria-activedescendant={showList && results[active] ? `rx-drug-${active}` : undefined}
        placeholder={atLimit ? `Limit of ${MAX_ITEMS} medicines reached` : 'Type at least 2 letters — e.g. paracet'}
        value={q} disabled={disabled || atLimit}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
      />
      {showList && (
        <ul id="rx-drug-list" role="listbox" className="card-list card"
          style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, margin: 0, padding: 6, listStyle: 'none', maxHeight: 260, overflowY: 'auto', boxShadow: 'var(--shadow-md)' }}>
          {state === 'loading' && <li className="card-row-meta" style={{ padding: 8 }}>Searching…</li>}
          {state === 'error' && <li className="card-row-meta" style={{ padding: 8 }}>Search is unavailable — press Enter to add “{q.trim()}” as typed.</li>}
          {state === 'done' && results.length === 0 && (
            <li className="card-row-meta" style={{ padding: 8 }}>No match in the catalogue. Press Enter to add “{q.trim()}” as typed.</li>
          )}
          {results.map((d, i) => (
            <li key={d.id || `${d.name}-${i}`} id={`rx-drug-${i}`} role="option" aria-selected={i === active}
              className="card-row" style={{ cursor: 'pointer', background: i === active ? 'var(--bg-hover)' : undefined }}
              onMouseDown={e => { e.preventDefault(); pick(d); }} onMouseEnter={() => setActive(i)}>
              <div className="card-row-body">
                <div className="card-row-title">{d.name}{d.strength ? ` ${d.strength}` : ''}</div>
                <div className="card-row-meta">{[d.form, d.genericName].filter(Boolean).join(' · ')}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Builder ──────────────────────────────────────────────────────────────── */
const PrescriptionBuilder = forwardRef(function PrescriptionBuilder(
  {
    patientId, visitId, diagnosisText, advice, allergies, initial, readOnly = false, onSaved,
    onItemsChange, noMedicines = false, onNoMedicinesChange, active = true,
  }, ref,
) {
  const { toast } = useToast();
  const [rx, setRx] = useState(initial || null);            // last server copy
  const [items, setItems] = useState(() => (initial ? itemsFromPrescription(initial) : []));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [advisory, setAdvisory] = useState({ allergyConflicts: [], duplicates: [], failed: false });
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const rxRef = useRef(rx);
  rxRef.current = rx;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // After signing, the panel reloads the prescriptions; adopt the signed copy so the
  // Rx number shows (this component only reads `initial` on mount otherwise).
  useEffect(() => {
    if (initial?.status === 'SIGNED') { setRx(initial); setItems(itemsFromPrescription(initial)); setDirty(false); }
  }, [initial]);

  const locked = readOnly || rx?.status === 'SIGNED';
  const validation = useMemo(() => validateItems(items), [items]);

  useEffect(() => { onItemsChange?.(items); }, [items, onItemsChange]);

  const edit = useCallback(updater => { setItems(updater); setDirty(true); }, []);
  const patchRow = (i, patch) => edit(list => list.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  // Advisory interaction check — debounced, never blocks.
  const namesKey = drugNamesKey(items);
  useEffect(() => {
    const names = checkableDrugNames(itemsRef.current);
    if (!patientId || names.length === 0) { setAdvisory({ allergyConflicts: [], duplicates: [], failed: false }); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      checkPrescription(patientId, names)
        .then(r => live && setAdvisory({
          allergyConflicts: r?.allergyConflicts || [], duplicates: r?.duplicates || [], failed: false,
        }))
        .catch(() => live && setAdvisory(a => ({ ...a, failed: true })));
    }, CHECK_DEBOUNCE_MS);
    return () => { live = false; clearTimeout(t); };
  }, [namesKey, patientId]);

  // Save current rows. Returns the saved Prescription, or null if nothing to save.
  const save = useCallback(async ({ silent = false } = {}) => {
    const list = itemsRef.current;
    if (list.length === 0 && !rxRef.current) return null;
    const v = validateItems(list);
    if (!v.ok) {
      setShowErrors(true);
      throw Object.assign(new Error(v.formError || 'Fix the highlighted medicine rows first'), { validation: true });
    }
    setSaving(true);
    try {
      const payload = buildPrescriptionBody({ patientId, visitId, diagnosisText, advice, items: list });
      const saved = rxRef.current?.id
        ? await updatePrescription(rxRef.current.id, payload)
        : await createPrescription(payload);
      setRx(saved); setDirty(false); setShowErrors(false);
      onSaved?.(saved);
      if (!silent) toast.success('Prescription draft saved');
      return saved;
    } finally {
      setSaving(false);
    }
  }, [patientId, visitId, diagnosisText, advice, onSaved, toast]);

  useImperativeHandle(ref, () => ({
    async ensureSaved() {
      if (locked) return null;
      if (itemsRef.current.length === 0) return rxRef.current?.id || null;
      if (!rxRef.current?.id || dirtyRef.current) return (await save({ silent: true }))?.id || null;
      return rxRef.current.id;
    },
    hasUnsavedProblems: () => itemsRef.current.length > 0 && !validateItems(itemsRef.current).ok,
    addMedicines(source) {
      const result = mergeCopiedMedicines(itemsRef.current, source);
      if (result.added > 0) edit(() => result.items);
      return result;
    },
  }), [save, locked, edit]);

  const onSaveClick = async () => {
    try { await save(); } catch (err) {
      if (err.validation) toast.warning(err.message); else toast.error(errMsg(err, "Couldn't save the prescription — please try again"));
    }
  };

  const onPdf = async () => {
    setPdfBusy(true);
    try {
      let id = rxRef.current?.id;
      if (!locked && (dirtyRef.current || !id)) id = (await save({ silent: true }))?.id;
      if (!id) { toast.info('Add a medicine first'); return; }
      await openPrescriptionPdf(id, `${rxRef.current?.rxNumber || 'prescription-draft'}.pdf`);
    } catch (err) {
      if (err.validation) toast.warning(err.message); else toast.error("Couldn't open the PDF — please try again");
    } finally { setPdfBusy(false); }
  };

  const hasAdvisory = advisory.allergyConflicts.length > 0 || advisory.duplicates.length > 0;

  return (
    <section className="card consult-work rx-builder" data-testid="prescription-builder" aria-labelledby={active ? 'consult-step-title' : undefined}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h2 className="consult-work__title" id={active ? 'consult-step-title' : undefined} tabIndex={-1} style={{ marginBottom: 0 }}>Prescription</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {rx?.rxNumber && <span className="badge badge-active mono">{rx.rxNumber}</span>}
          <span className={`badge ${locked ? 'badge-active' : 'badge-pending'}`}>{locked ? 'Signed' : 'Draft'}</span>
        </div>
      </div>
      <div className="consult-work__why">The patient sees the medicines once you sign. A visit can be signed without a prescription.</div>

      <div className={`alert ${allergies || allergies === null ? 'alert-warning' : 'alert-info'}`} style={{ margin: '12px 0' }} role="note">
        <div className="grow">
          <strong>Patient allergies:</strong>{' '}
          {allergies === undefined ? 'Checking…'
            : allergies === null ? 'Not available — check the patient record before prescribing'
              : (allergies || 'No allergies recorded')}
        </div>
      </div>

      {hasAdvisory && (
        <div className="alert alert-warning" role="status" style={{ marginBottom: 12 }}>
          <div className="grow">
            <div className="alert-title">Check before you sign</div>
            {advisory.allergyConflicts.map((c, i) => (
              <div key={`a${i}`}>{c.drug} may conflict with the recorded allergy “{c.allergy}”.</div>
            ))}
            {advisory.duplicates.map((d, i) => <div key={`d${i}`}>{d} appears more than once.</div>)}
            <div className="form-hint">Advisory only — not a substitute for clinical judgement. It never blocks signing.</div>
          </div>
        </div>
      )}
      {advisory.failed && !hasAdvisory && (
        <div className="form-hint" style={{ marginBottom: 8 }}>The allergy / duplicate check is unavailable right now.</div>
      )}

      {!locked && (
        <DrugSearch disabled={saving} atLimit={items.length >= MAX_ITEMS}
          onPick={drug => edit(list => (list.length >= MAX_ITEMS ? list : [...list, itemFromDrug(drug)]))} />
      )}

      {items.length === 0 ? (
        <div className="empty-state"><div className="empty-title">No medicines yet</div>
          <div className="empty-desc">{locked ? 'No prescription was issued for this visit.' : 'Search above to add the first one, or mark that none are needed.'}</div></div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
          <table className="rx-table">
            <thead>
              <tr><th>Medicine</th><th>Dose</th><th>Frequency</th><th>Days</th><th>Instructions</th>{!locked && <th aria-label="Actions" />}</tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const e = showErrors ? validation.itemErrors[i] || {} : {};
                const customFreq = !FREQUENCY_PRESETS.includes(it.frequency);
                return (
                  <tr key={it.key}>
                    <td data-label="Medicine" style={{ minWidth: 140 }}>
                      {locked ? <strong>{it.drugName} {it.strength}</strong> : (
                        <>
                          <input className="form-input" aria-label={`Medicine ${i + 1} name`} value={it.drugName}
                            onChange={ev => patchRow(i, { drugName: ev.target.value })} />
                          {e.drugName && <div className="form-hint" role="alert">{e.drugName}</div>}
                          {it.strength && <div className="card-row-meta">{it.strength}{it.form ? ` · ${it.form}` : ''}</div>}
                        </>
                      )}
                    </td>
                    <td data-label="Dose" style={{ minWidth: 96 }}>
                      {locked ? it.dose : (
                        <>
                          <input className="form-input" aria-label={`Medicine ${i + 1} dose`} value={it.dose}
                            onChange={ev => patchRow(i, { dose: ev.target.value })} />
                          {e.dose && <div className="form-hint" role="alert">{e.dose}</div>}
                        </>
                      )}
                    </td>
                    <td data-label="Frequency" style={{ minWidth: 168 }}>
                      {locked ? <>{it.frequency}<div className="card-row-meta">{frequencyText(it.frequency)}</div></> : (
                        <>
                          <div role="group" aria-label={`Medicine ${i + 1} frequency`} style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                            {FREQUENCY_PRESETS.map(p => (
                              <button key={p} type="button" aria-pressed={it.frequency === p}
                                className={`btn btn-sm ${it.frequency === p ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => patchRow(i, { frequency: p })}>{p}</button>
                            ))}
                          </div>
                          <input className="form-input" aria-label={`Medicine ${i + 1} custom frequency`}
                            aria-invalid={!isValidFrequency(it.frequency)} placeholder="Custom, e.g. 0.5-0-1"
                            value={customFreq ? it.frequency : ''}
                            onChange={ev => patchRow(i, { frequency: ev.target.value })} />
                          {(e.frequency || (customFreq && it.frequency && !isValidFrequency(it.frequency))) && (
                            <div className="form-hint" role="alert">{validation.itemErrors[i]?.frequency}</div>
                          )}
                          {frequencyText(it.frequency) && <div className="card-row-meta">{frequencyText(it.frequency)}</div>}
                        </>
                      )}
                    </td>
                    <td data-label="Days" style={{ minWidth: 72 }}>
                      {locked ? it.durationDays : (
                        <>
                          <input className="form-input" type="number" min="1" max="365" inputMode="numeric"
                            aria-label={`Medicine ${i + 1} duration in days`} value={it.durationDays}
                            onChange={ev => patchRow(i, { durationDays: ev.target.value })} />
                          {e.durationDays && <div className="form-hint" role="alert">{e.durationDays}</div>}
                        </>
                      )}
                    </td>
                    <td data-label="Instructions" style={{ minWidth: 120 }}>
                      {locked ? (it.instructions || '—') : (
                        <>
                          <input className="form-input" maxLength={200} aria-label={`Medicine ${i + 1} instructions`}
                            placeholder="e.g. after food" value={it.instructions}
                            onChange={ev => patchRow(i, { instructions: ev.target.value })} />
                          {e.instructions && <div className="form-hint" role="alert">{e.instructions}</div>}
                        </>
                      )}
                    </td>
                    {!locked && (
                      <td data-label="" style={{ whiteSpace: 'nowrap' }}>
                        <div className="rx-actions">
                        <button type="button" className="btn btn-ghost btn-sm" aria-label={`Move medicine ${i + 1} up`}
                          disabled={i === 0} onClick={() => edit(l => moveItem(l, i, i - 1))}>↑</button>
                        <button type="button" className="btn btn-ghost btn-sm" aria-label={`Move medicine ${i + 1} down`}
                          disabled={i === items.length - 1} onClick={() => edit(l => moveItem(l, i, i + 1))}>↓</button>
                        <button type="button" className="btn btn-ghost btn-sm" aria-label={`Remove medicine ${i + 1}`}
                          onClick={() => edit(l => l.filter((_, idx) => idx !== i))}>Remove</button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!locked && items.length === 0 && (
        <label className="form-check" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={noMedicines} onChange={e => onNoMedicinesChange?.(e.target.checked)} />
          <span className="field-text">No medicines needed for this visit</span>
        </label>
      )}

      {showErrors && validation.formError && <div className="alert alert-error" role="alert" style={{ marginTop: 10 }}>{validation.formError}</div>}

      {items.length > 0 && (
        <div className="field-group" style={{ marginTop: 16 }}>
          <div>
            <button type="button" className="consult-link" aria-expanded={showPreview} aria-controls="rx-preview"
              onClick={() => setShowPreview(p => !p)}>{showPreview ? 'Hide preview' : 'Show preview'}</button>
          </div>
          {showPreview && <div className="card" id="rx-preview" style={{ background: 'var(--bg-subtle)', boxShadow: 'none' }} aria-label="Prescription preview">
            <div className="card-row-meta">℞ {rx?.rxNumber || 'Draft — not yet signed'}</div>
            <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {items.filter(i => i.drugName.trim()).map(i => <li key={i.key} className="field-text">{describeItem(i)}{i.instructions ? ` · ${i.instructions}` : ''}</li>)}
            </ol>
            {(advice || '').trim() && <div className="field-text" style={{ marginTop: 8 }}><strong>Advice:</strong> {advice}</div>}
          </div>}
        </div>
      )}

      <div className="form-actions" style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {!locked && (
          <button type="button" className="btn btn-primary btn-sm" disabled={saving || (items.length === 0 && !rx)} onClick={onSaveClick}>
            {saving ? 'Saving…' : dirty || !rx ? 'Save draft' : 'Draft saved'}
          </button>
        )}
        <button type="button" className="btn btn-secondary btn-sm" disabled={pdfBusy || items.length === 0} onClick={onPdf}>
          {pdfBusy ? 'Preparing PDF…' : 'Print / PDF'}
        </button>
        {!locked && dirty && <span className="card-row-meta" style={{ alignSelf: 'center' }}>Unsaved changes — saved automatically when you sign.</span>}
      </div>
    </section>
  );
});

export default PrescriptionBuilder;
